import { useState, useEffect } from 'react';
import axios from 'axios';
import { getCachedValue, setCachedValue } from '../utils/requestCache';
import { checkRateLimit } from '../utils/requestRateLimiter';
import {
  getInFlightRequest,
  setInFlightRequest,
  retainInFlightRequest,
  releaseInFlightRequest,
  settleInFlightRequest
} from '../utils/inFlightRequests';

const RATE_LIMIT_MESSAGE = 'Too many requests. Please wait a moment and try again.';

const useSmartyLookupBase = ({
  isEnabled,
  debounceMs,
  cacheNamespace,
  cacheTtlMs,
  rateLimitNamespace,
  rateLimitConfig,
  cacheKey,
  requestUrl,
  transformResponse,
  onError = null,
  effectDeps = []
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rateLimited, setRateLimited] = useState(false);
  const [responseTime, setResponseTime] = useState(null);

  useEffect(() => {
    if (!isEnabled) {
      setSuggestions([]);
      setLoading(false);
      setError(null);
      setRateLimited(false);
      setResponseTime(null);
      return;
    }

    setLoading(true);
    setError(null);
    let cancelled = false;
    let activeRequest = null;
    let activeRequestKey = null;

    const timer = setTimeout(async () => {
      try {
        const cached = getCachedValue(cacheNamespace, cacheKey);
        if (cached) {
          if (!cancelled) {
            setSuggestions(cached.suggestions);
            setResponseTime(cached.responseTime);
            setRateLimited(false);
            setLoading(false);
          }
          return;
        }

        const rateLimitStatus = checkRateLimit(rateLimitNamespace, rateLimitConfig);
        if (!rateLimitStatus.allowed) {
          if (!cancelled) {
            setSuggestions([]);
            setResponseTime(null);
            setRateLimited(true);
            setError(RATE_LIMIT_MESSAGE);
            setLoading(false);
          }
          return;
        }

        activeRequestKey = cacheKey;
        const existingRequest = getInFlightRequest(activeRequestKey);
        let response;
        let elapsedTime;

        if (existingRequest) {
          activeRequest = retainInFlightRequest(activeRequestKey);
          ({ response, elapsedTime } = await activeRequest.promise);
        } else {
          const controller = new AbortController();
          const request = {
            controller,
            settled: false,
            promise: (async () => {
              const startTime = performance.now();
              const networkResponse = await axios.get(requestUrl, { signal: controller.signal });
              const endTime = performance.now();
              return {
                response: networkResponse,
                elapsedTime: Math.round(endTime - startTime)
              };
            })()
          };
          setInFlightRequest(activeRequestKey, request);
          activeRequest = retainInFlightRequest(activeRequestKey);
          try {
            ({ response, elapsedTime } = await activeRequest.promise);
          } finally {
            settleInFlightRequest(activeRequestKey, activeRequest);
          }
        }

        if (cancelled) {
          return;
        }

        const formattedSuggestions = transformResponse(response.data);
        setResponseTime(elapsedTime);
        setCachedValue(cacheNamespace, cacheKey, {
          suggestions: formattedSuggestions,
          responseTime: elapsedTime
        }, cacheTtlMs);
        setRateLimited(false);
        setSuggestions(formattedSuggestions);
      } catch (err) {
        if (cancelled || err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
          return;
        }
        onError?.(err);
        setRateLimited(false);
        setError(err.message);
        setSuggestions([]);
        setResponseTime(null);
      } finally {
        setLoading(false);
      }
    }, debounceMs);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (activeRequestKey && activeRequest) {
        releaseInFlightRequest(activeRequestKey, activeRequest);
      }
      if (activeRequest?.subscribers === 0 && !activeRequest?.settled) {
        activeRequest.controller.abort();
      }
    };
  }, [isEnabled, debounceMs, ...effectDeps]);

  return {
    suggestions,
    loading,
    error,
    rateLimited,
    responseTime
  };
};

export default useSmartyLookupBase;
